import Tesseract from 'tesseract.js';
import { parseReceiptText } from '../utils/receiptParser.js';

// The reading rules live in utils/receiptParser.js (shared with the server); re-exported for callers.
export { parseMerchant, parseAmount, parseCurrency, parseDate, parseCategory, parseReceiptText } from '../utils/receiptParser.js';

// One OCR worker is created on first use and kept: creating one per receipt re-downloads
// and re-initialises the engine and the language data every time (seconds per receipt).
//
// The engine and its language data come from a CDN the first time. With no route to it the
// library waits forever instead of failing, so every wait here has a limit: a stuck receipt
// must never freeze the upload or a whole bulk import behind it.
const INIT_TIMEOUT_MS = 60000;   // first start: downloads a few MB
const READ_TIMEOUT_MS = 45000;   // one receipt
const FAST_FAIL_MS = 120000;     // after a failed start, say so at once instead of waiting again for every file

let workerPromise = null;
let startFailure = null;         // { at, error }
let currentProgress = null;

const withTimeout = (promise, ms, message) => {
    let timer;
    const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), ms); });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const dropWorker = () => {
    const w = workerPromise;
    workerPromise = null;
    if (w) w.then(worker => worker.terminate()).catch(() => {});
};

const getWorker = () => {
    if (startFailure && Date.now() - startFailure.at < FAST_FAIL_MS) return Promise.reject(startFailure.error);
    if (!workerPromise) {
        const starting = withTimeout(Tesseract.createWorker('eng', 1, {
            logger: (m) => { if (currentProgress && m.status === 'recognizing text') currentProgress(Math.round(m.progress * 100)); },
        }), INIT_TIMEOUT_MS, 'The text reader could not start (is the network blocked?)');
        workerPromise = starting;
        starting.catch((error) => { startFailure = { at: Date.now(), error }; if (workerPromise === starting) workerPromise = null; });
    }
    return workerPromise;
};

const MAX_SIDE = 2000;      // enough for receipt text, and keeps OCR and storage small
const JPEG_QUALITY = 0.85;

/**
 * Shrink a phone photo before OCR and storage: a 12 MP photo is 3-8 MB, slow to read,
 * and over the server's 5 MB receipt limit. PDFs and small images are returned as they are.
 */
export const prepareReceiptImage = async (file) => {
    if (!file || !file.type?.startsWith('image/') || typeof createImageBitmap !== 'function' || typeof document === 'undefined') return file;
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        if (scale === 1 && file.size <= 1500000) { bitmap.close?.(); return file; }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff'; // transparent PNGs would otherwise turn black in JPEG
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close?.();
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        return blob || file;
    } catch {
        return file;
    }
};

// Extract data from receipt image
export const extractReceiptData = async (imageFile, onProgress) => {
    try {
        if (imageFile instanceof File || imageFile instanceof Blob) {
           if (imageFile.size < 10) throw new Error('Image file is too small');
        }

        const worker = await getWorker();
        currentProgress = onProgress || null;
        let text;
        try {
            ({ data: { text } } = await withTimeout(worker.recognize(imageFile), READ_TIMEOUT_MS, 'Reading the receipt took too long'));
        } catch (error) {
            dropWorker(); // a stuck job would hold every later one up; start fresh next time
            throw error;
        } finally {
            currentProgress = null;
        }

        return parseReceiptText(text);
    } catch (error) {
        console.error('OCR Error:', error);
        throw error;
    }
};

// Convert image file to data URL for storage
export const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};
